import { AccountConnection } from 'src/interfaces/accountConnection.interface';
import { AzureVoice, Voice } from 'src/interfaces/voice.interface';
import { post } from './requests';

declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext;
  }
}

const playTTS = async (
  voice: Voice,
  text: string,
  envId: string,
  provider?: AccountConnection,
  isProviderConnected?: boolean,
) => {
  try {
    let response;

    switch (voice.type) {
      case 'voice_azure':
        const headers = {
          'Ocp-Apim-Subscription-Key': provider?.credentials.api_key,
          'Content-Type': 'application/ssml+xml',
          'X-Microsoft-OutputFormat': 'audio-16khz-128kbitrate-mono-mp3',
        };

        const body = `<speak version='1.0' xml:lang='en-US'>
              <voice xml:lang='en-US' name='${voice.voice_name}'>
                <prosody rate='${(voice as AzureVoice).rate || 0}%' pitch='${(voice as AzureVoice).pitch || 0}%'>
                  ${text}
                </prosody>
              </voice>
            </speak>`;

        if (!isProviderConnected) {
          const responseAzure = await post(
            `/voices/global/listen`,
            {
              headers,
              body,
              type: 'voice_azure',
            },
            { envId },
          );

          return playArrayBuffer(responseAzure.data as ArrayBuffer);
        } else {
          response = await fetch(
            `https://${provider?.credentials.region}.tts.speech.microsoft.com/cognitiveservices/v1`,
            {
              method: 'POST',
              headers: {
                'Ocp-Apim-Subscription-Key':
                  provider?.credentials.api_key || '',
                'Content-Type': 'application/ssml+xml',
                'X-Microsoft-OutputFormat': 'audio-16khz-128kbitrate-mono-mp3',
              },
              body: `<speak version='1.0' xml:lang='en-US'>
                <voice xml:lang='en-US' name='${voice.voice_name}'>
                  <prosody rate='${(voice as AzureVoice).rate || 0}%' pitch='${(voice as AzureVoice).pitch || 0}%'>
                    ${text}
                  </prosody>
                </voice>
              </speak>`,
            },
          );
        }
        break;

      case 'voice_eleven_labs':
        if (!isProviderConnected) {
          const responseAzure = await post(
            `/voices/global/listen`,
            {
              type: 'voice_eleven_labs',
              voice_id: voice.voice_id,
              text,
              model_id: voice.model_id,
              stability: voice.stability,
              similarity_boost: voice.similarity_boost,
            },
            { envId },
          );

          return playArrayBuffer(responseAzure.data as ArrayBuffer);
        } else {
          response = await fetch(
            `https://api.elevenlabs.io/v1/text-to-speech/${voice.voice_id}`,
            {
              method: 'POST',
              headers: {
                'xi-api-key': provider?.credentials.api_key || '',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                text,
                model_id: voice.model_id,
                stability: voice.stability,
                similarity_boost: voice.similarity_boost,
              }),
            },
          );
        }
        break;

      case 'voice_cartesia':
        if (!isProviderConnected) {
          const responseAzure = await post(
            `/voices/global/listen`,
            {
              type: 'voice_cartesia',
              body: {
                model_id: voice.model || 'sonic-english',
                transcript: text,
                voice: {
                  mode: 'id',
                  id: voice.voice_id,
                },
                output_format: {
                  container: 'mp3',
                  bit_rate: 128000,
                  sample_rate: 44100,
                },
                language: 'en',
              },
            },
            { envId },
          );

          return playArrayBuffer(responseAzure.data as ArrayBuffer);
        } else {
          response = await fetch('https://api.cartesia.ai/tts/bytes', {
            method: 'POST',
            headers: {
              'Cartesia-Version': '2024-06-10',
              'X-API-Key': provider?.credentials.api_key || '',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              model_id: voice.model || 'sonic-english',
              transcript: text,
              voice: {
                mode: 'id',
                id: voice.voice_id,
              },
              output_format: {
                container: 'mp3',
                bit_rate: 128000,
                sample_rate: 44100,
              },
              language: 'en',
            }),
          });
        }
        break;

      case 'voice_rime': {
        if (!isProviderConnected) {
          const responseRime = await post(
            '/voices/global/listen',
            {
              type: 'voice_rime',
              body: {
                text,
                speaker: voice.speaker,
                model_id: voice.model_id,
                speed_alpha: voice.speed_alpha,
              },
            },
            {
              envId,
            },
          );

          return playArrayBuffer(responseRime.data as ArrayBuffer);
        } else {
          response = await fetch('https://users.rime.ai/v1/rime-tts', {
            method: 'POST',
            headers: {
              accept: 'audio/mpeg',
              Authorization: `Bearer ${provider?.credentials.api_key}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              text,
              speaker: voice.speaker,
              model_id: voice.model_id,
              speed_alpha: voice.speed_alpha,
            }),
          });
        }

        const base64Audio = await response.json();
        const audioData = atob(base64Audio.audioContent);
        const arrayBuffer = new Uint8Array(audioData.length);
        for (let i = 0; i < audioData.length; i++) {
          arrayBuffer[i] = audioData.charCodeAt(i);
        }
        response = new Response(arrayBuffer.buffer);
        break;
      }

      default:
        throw new Error('Unsupported voice type');
    }

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const audioArrayBuffer = await response.arrayBuffer();

    return playArrayBuffer(audioArrayBuffer);
  } catch (error) {
    console.error('TTS Error:', error);
    return false;
  }
};

const playArrayBuffer = async (arrayBuffer: ArrayBuffer) => {
  const uint8Array = new Uint8Array(arrayBuffer);
  const audioBlob = new Blob([uint8Array], {
    type: 'audio/mpeg',
  });
  const audioUrl = URL.createObjectURL(audioBlob);
  const audio = new Audio(audioUrl);

  audio.addEventListener('ended', () => {
    URL.revokeObjectURL(audioUrl);
  });

  await audio.play();

  return true;
};

export const listenVoice = async (
  voice: Voice,
  text: string,
  envId: string,
  provider?: AccountConnection,
  isProviderConnected?: boolean,
) => {
  return await playTTS(voice, text, envId, provider, isProviderConnected);
};
